import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * The booking in progress, kept on the device.
 *
 * A booking is two minutes of typing with a customer standing at the counter, and the phone it
 * is typed on has a back gesture on the edge of the screen. Losing that work to an accidental
 * swipe is the difference between a shop using the product and a shop keeping a paper book
 * next to it — so every keystroke is written to `sessionStorage` and read back on mount.
 *
 * `sessionStorage` rather than `localStorage` on purpose: a draft is meant to survive a
 * navigation, a refresh and a mis-swipe within this sitting. It is NOT meant to resurface next
 * week on a shared shop tablet, half-filled with a customer nobody remembers.
 *
 * The write is debounced because it happens on every keystroke and JSON-serialising the whole
 * bill on each one is work the main thread is better off not doing while somebody types.
 */

const STORAGE_KEY = 'tailoring-crm:booking-draft';
const WRITE_DEBOUNCE_MS = 300;

export interface BookingDraft<T> {
  value: T;
  /** True when this session was resumed from storage rather than started fresh. */
  restored: boolean;
}

function read<T>(key: string): T | null {
  try {
    const raw = globalThis.sessionStorage?.getItem(key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    // A corrupt or unavailable store is not worth failing a booking over — start fresh.
    return null;
  }
}

export interface UseBookingDraftResult<T> {
  draft: T;
  setDraft: (next: T | ((previous: T) => T)) => void;
  /** True when the draft on screen was recovered from a previous visit to this screen. */
  restored: boolean;
  /** Throw the draft away — after a successful booking, or when the shop starts again. */
  clear: () => void;
}

export function useBookingDraft<T>(initial: T, key = STORAGE_KEY): UseBookingDraftResult<T> {
  const stored = useRef<T | null>(read<T>(key));
  // The empty draft, captured once. Callers build it as a literal, so depending on it directly
  // would reset the form on every render.
  const blank = useRef(initial);
  const [restored, setRestored] = useState(stored.current !== null);
  const [draft, setDraftState] = useState<T>(stored.current ?? initial);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const cleared = useRef(false);

  useEffect(() => {
    if (cleared.current) return;
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      try {
        globalThis.sessionStorage?.setItem(key, JSON.stringify(draft));
      } catch {
        // Storage full or blocked (private browsing). The booking still works; it just will
        // not survive a back gesture, which is strictly better than refusing to take it.
      }
    }, WRITE_DEBOUNCE_MS);

    return () => {
      if (timer.current) clearTimeout(timer.current);
    };
  }, [draft, key]);

  const setDraft = useCallback((next: T | ((previous: T) => T)) => {
    cleared.current = false;
    setDraftState((previous) =>
      typeof next === 'function' ? (next as (previous: T) => T)(previous) : next,
    );
    // Once the shop types again, the "we restored this" notice has served its purpose.
    setRestored(false);
  }, []);

  const clear = useCallback(() => {
    cleared.current = true;
    if (timer.current) clearTimeout(timer.current);
    try {
      globalThis.sessionStorage?.removeItem(key);
    } catch {
      // Nothing to do: the draft is gone from memory either way.
    }
    setRestored(false);
    setDraftState(blank.current);
    // The next edit should persist again rather than be swallowed by the cleared flag.
    cleared.current = false;
  }, [key]);

  return { draft, setDraft, restored, clear };
}
